import { css } from "@emotion/css";
import { DOMAttributes, FocusableElement } from "@react-types/shared";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { AriaDialogProps, useDialog } from "react-aria";

import { GhostIconButton } from "../../components/button/GhostIconButton";
import { useMediaQuery } from "../../components/utils";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";

interface ModalProps extends AriaDialogProps {
  children?: React.ReactNode;
  width?: string;
  testID?: string;
}
export interface ModalHeaderProps {
  title: string;
  close: () => void;
  hideDivider?: boolean;
  testID?: string;
}
export interface ModalContentProps {
  children: string | React.ReactNode;
  testID?: string;
}
export interface ModalFooterProps {
  children?: React.ReactNode;
  className?: string;
  testID?: string;
}

export type ModalScroll = "inside" | "viewport";
export interface ModalShadowState {
  top: boolean;
  bottom: boolean;
}

/**
 * @deprecated use the new Modal components based on React Aria Components instead
 */
export const ModalContext = React.createContext<
  DOMAttributes<FocusableElement> | undefined
>(undefined);

/**
 * @deprecated use the new Modal components based on React Aria Components instead
 */
export function useModal() {
  return React.useContext(ModalContext);
}

/**
 * @deprecated use the new Modal components based on React Aria Components instead
 */
export function LegacyModal({
  children,
  width = "520px",
  testID = "modal",
  ...props
}: ModalProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const { dialogProps, titleProps } = useDialog(props, ref);
  const { mediaQuery } = useMediaQuery();

  return (
    <ModalContext.Provider value={titleProps}>
      <div
        {...dialogProps}
        ref={ref}
        className={css(
          styles.modal,
          mediaQuery({
            width: ["100vw", width],
            borderBottomLeftRadius: [0, "var(--border-radius-3)"],
            borderBottomRightRadius: [0, "var(--border-radius-3)"],
          }),
          {
            backgroundColor: "var(--surface-base)",
            boxShadow: "var(--shadow-modal)",
          }
        )}
        data-testid={testID}
      >
        {children}
      </div>
    </ModalContext.Provider>
  );
}

/**
 * @deprecated use the new Modal components based on React Aria Components instead
 */
export function ModalHeader({
  title,
  close,
  hideDivider,
  testID = "modal-header",
}: ModalHeaderProps) {
  const titleProps = useModal();
  const colors = useColors();

  return (
    <div
      className={css(
        styles.header,
        !hideDivider && { borderBottom: `1px solid ${colors["border-base"]}` }
      )}
    >
      <Text
        {...titleProps}
        style="global/modal-header"
        testID={`${testID}-header`}
      >
        {title}
      </Text>
      <GhostIconButton
        size="small"
        onPress={close}
        icon="close"
        color="icon-base"
        testID={`${testID}-close-button`}
      />
    </div>
  );
}

/**
 * @deprecated use the new Modal components based on React Aria Components instead
 */
export function ModalContent({ children, testID }: ModalContentProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isShadowsVisible, setIsShadowsVisible] = useState<ModalShadowState>({
    top: true,
    bottom: true,
  });
  const colors = useColors();
  const checkIfShadowsVisible = () => {
    const intermediateState = {
      top: true,
      bottom: true,
    };

    if (ref && ref.current) {
      intermediateState.bottom =
        ref.current?.scrollHeight - ref.current?.scrollTop !==
        ref.current?.clientHeight;
      intermediateState.top = ref.current?.scrollTop !== 0;
    }

    setIsShadowsVisible(intermediateState);
  };

  useEffect(() => checkIfShadowsVisible(), []);

  const handleScroll = () => checkIfShadowsVisible();

  return (
    <div
      className={classNames(
        css(styles.content, {
          borderBottom: `1px solid ${colors["border-base"]}`,
          boxShadow: `${
            isShadowsVisible.top
              ? `inset 0px 11px 8px -10px rgba(0, 0, 0, 0.08) ${
                  isShadowsVisible.bottom ? " ," : ""
                }`
              : ""
          } ${
            isShadowsVisible.bottom
              ? "inset 0px -11px 8px -10px rgba(0, 0, 0, 0.08)"
              : ""
          }`,
        })
      )}
      ref={ref}
      onScroll={handleScroll}
    >
      <Text style="global/body-primary" testID={`${testID}-content`}>
        {children}
      </Text>
    </div>
  );
}

/**
 * @deprecated use the new Modal components based on React Aria Components instead
 */
export function ModalFooter({
  children,
  className,
  testID = "modal-footer",
}: ModalFooterProps) {
  const { mediaQuery } = useMediaQuery();

  return (
    <div
      data-testid={testID}
      className={css(
        styles.footer,
        className,
        css(
          mediaQuery({
            borderBottomLeftRadius: [0, "var(--border-radius-3)"],
            borderBottomRightRadius: [0, "var(--border-radius-3)"],
          })
        )
      )}
    >
      {children}
    </div>
  );
}

const styles = {
  modal: css({
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    outline: "none",
    borderRadius: "var(--border-radius-3)",
    overflow: "hidden",
  }),
  header: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopLeftRadius: "var(--border-radius-3)",
    borderTopRightRadius: "var(--border-radius-3)",
    padding: "var(--space-3)",
    paddingLeft: "var(--space-5)",
    paddingRight: "var(--space-5)",
  }),
  content: css({
    padding: "var(--space-5)",
  }),
  footer: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    borderBottomLeftRadius: "var(--border-radius-3)",
    borderBottomRightRadius: "var(--border-radius-3)",
    padding: "var(--space-4)",
  }),
};
